import {createReducer} from "redux-act";
import {
	IContest,
	IContestReducer,
	IPlayerStatMapperEntity,
	IReducerAction,
	IServerPrediction,
	IShowMyResponse,
	Nullable,
} from "modules/types";
import {
	addPointToScore,
	addPointToStrikes,
	clearContestPointsSuccess,
	fetchContestResultsClear,
	fetchContestResultsRequest,
	fetchContestResultsSuccess,
	fetchContestsSuccess,
	gameOver,
	pushPlayerToCardsArray,
	putPlayerStat,
	setActiveQuestion,
	setCurrentQuestionIndex,
	setGameStarted,
	setIsButtonsDisabled,
	setTimerTime,
	setTimerTimeLimit,
	toggleTimerVisibility,
} from "modules/actions";
import {RequestState} from "modules/enums";
import {uniq} from "lodash";

const initialState: IContestReducer = {
	contests: [],
	currentQuestion: null,
	playersCardsIds: [],
	currentQuestionIndex: 0,
	isGameStarted: false,
	playerStatsMapper: {},
	isTimerVisible: false,
	isButtonsDisabled: false,
	contestResult: null,
	isContestCleared: false,
	timerTime: null,
	currentTimerTimeLimit: 10,

	requestStates: {
		showMy: RequestState.IDLE,
	},
};
type IContestReducerPayload<TP = null> = IReducerAction<IContestReducer, TP>;

const onFetchContestsSuccess: IContestReducerPayload<IContest[]> = (state, payload) => ({
	...state,
	contests: payload,
});

const onSetActiveQuestion: IContestReducerPayload<IServerPrediction> = (state, payload) => ({
	...state,
	currentQuestion: payload,
});
const onSetCurrentQuestionIndex: IContestReducerPayload<number> = (state, payload) => ({
	...state,
	currentQuestionIndex: payload,
});
const onPushPlayerToCardsArray: IContestReducerPayload<number> = (state, payload) => ({
	...state,
	playersCardsIds: uniq(state.playersCardsIds.concat(payload)),
});

const onSetGameStarted: IContestReducerPayload<boolean> = (state, payload) => ({
	...state,
	isGameStarted: payload,
});

const onPutPlayerStat: IContestReducerPayload<IPlayerStatMapperEntity> = (state, payload) => ({
	...state,
	playerStatsMapper: {...state.playerStatsMapper, [payload.playerId]: payload.stat},
});

const onToggleTimerVisibility: IContestReducerPayload<boolean> = (state, payload) => ({
	...state,
	isTimerVisible: payload,
});

const onSetIsButtonsDisabled: IContestReducerPayload<boolean> = (state, payload) => ({
	...state,
	isButtonsDisabled: payload,
});

const onGameOver: IContestReducerPayload = (state) => ({
	...state,
	isButtonsDisabled: true,
	isTimerVisible: false,
	isGameStarted: false,
});

const onFetchContestResultsSuccess: IContestReducerPayload<IShowMyResponse> = (state, payload) => ({
	...state,
	contestResult: payload,
});

const onAddPointToScore: IContestReducerPayload = (state, payload) => ({
	...state,
	contestResult: {
		...state.contestResult!,
		gameBar: {
			...(state.contestResult!.gameBar ?? {}),
			contestPoints: (state.contestResult!.gameBar.contestPoints ?? 0) + 1,
		},
	},
});

const onAddPointToStrikes: IContestReducerPayload = (state, payload) => ({
	...state,
	contestResult: {
		...state.contestResult!,
		gameBar: {
			...(state.contestResult!.gameBar ?? {}),
			numberStrikes: (state.contestResult!.gameBar.numberStrikes ?? 0) + 1,
		},
	},
});

const onClearContestPointsSuccess: IContestReducerPayload = (state, payload) => ({
	...state,
	isContestCleared: true,
});

const onSetTimerTime: IContestReducerPayload<Nullable<number>> = (state, payload) => ({
	...state,
	timerTime: payload,
});

const onSetTimerTimeLimit: IContestReducerPayload<Nullable<number>> = (state, payload) => ({
	...state,
	currentTimerTimeLimit: payload,
});

const onFetchContestResultsRequest: IContestReducerPayload<Nullable<number>> = (
	state,
	payload
) => ({
	...state,
	requestStates: {
		...state.requestStates,
		showMy: RequestState.Requested,
	},
});

const onFetchContestResultsClear: IContestReducerPayload<Nullable<number>> = (state, payload) => ({
	...state,
	requestStates: {
		...state.requestStates,
		showMy: RequestState.IDLE,
	},
});

export const contest = createReducer({}, initialState)
	.on(fetchContestsSuccess, onFetchContestsSuccess)
	.on(setActiveQuestion, onSetActiveQuestion)
	.on(setCurrentQuestionIndex, onSetCurrentQuestionIndex)
	.on(pushPlayerToCardsArray, onPushPlayerToCardsArray)
	.on(setGameStarted, onSetGameStarted)
	.on(putPlayerStat, onPutPlayerStat)
	.on(toggleTimerVisibility, onToggleTimerVisibility)
	.on(setIsButtonsDisabled, onSetIsButtonsDisabled)
	.on(gameOver, onGameOver)
	.on(fetchContestResultsSuccess, onFetchContestResultsSuccess)
	.on(addPointToScore, onAddPointToScore)
	.on(addPointToStrikes, onAddPointToStrikes)
	.on(clearContestPointsSuccess, onClearContestPointsSuccess)
	.on(setTimerTime, onSetTimerTime)
	.on(setTimerTimeLimit, onSetTimerTimeLimit)
	.on(fetchContestResultsRequest, onFetchContestResultsRequest)
	.on(fetchContestResultsClear, onFetchContestResultsClear);
